import Hls, {
  AudioTrackLoadedData,
  BufferAppendedData,
  BufferCreatedTrackSet,
  LevelDetails,
  LevelPTSUpdatedData,
  LevelSwitchedData,
  LevelUpdatedData,
  MediaFragment,
  SubtitleTrackLoadedData,
} from 'hls.js';
import {
  buildAudioPlaylistRows,
  buildBufferRow,
  buildLevelPlaylistRows,
  buildSubtitlePlaylistRows,
  createStatsSnapshot,
  getBufferRowBufferedBytes,
  getFragmentBufferOverlapRatio,
  getFragmentBufferedBytesKey,
  getFragmentBufferedBytesRollupKey,
  getFragmentsForPlaylistRow,
  getPlaylistBufferedBytes,
  stripDeliveryDirectives,
  timeRangesToTuples,
} from './hls-timeline-stats';
import {
  HlsTimelineBufferRow,
  HlsTimelinePlaylistRow,
  HlsTimelineStats,
} from './hls-timeline-stats-types';

type BufferRowState = {
  id: string;
  bufferKind: HlsTimelineBufferRow['bufferKind'];
  trackId?: string;
  ranges: ReturnType<typeof timeRangesToTuples>;
};

/**
 * Maintains buffer and playlist row snapshots for the HLS timeline stats table.
 * Mirrors timeline chart data sources without depending on Chart.js.
 */
export class HlsTimelineStatsCollector {
  private readonly hls: Hls;
  private readonly bufferRowStates = new Map<string, BufferRowState>();
  private playlistRows: HlsTimelinePlaylistRow[] = [];
  private media: HTMLMediaElement | null = null;
  private readonly cleanupFns: Array<() => void> = [];
  private readonly bufferCleanupFns: Array<() => void> = [];
  private readonly fragmentChunkBytes = new Map<string, number>();

  constructor(hls: Hls) {
    this.hls = hls;
    this.media = hls.media;
  }

  /** Removes listeners registered for buffer row updates. */
  destroy() {
    this.clearBufferListeners();
    this.cleanupFns.forEach((cleanup) => cleanup());
    this.cleanupFns.length = 0;
    this.bufferRowStates.clear();
    this.playlistRows = [];
    this.fragmentChunkBytes.clear();
    this.media = null;
  }

  private clearBufferListeners() {
    this.bufferCleanupFns.forEach((cleanup) => cleanup());
    this.bufferCleanupFns.length = 0;
  }

  reset() {
    this.bufferRowStates.clear();
    this.playlistRows = [];
    this.fragmentChunkBytes.clear();
  }

  /** Tracks demuxed bytes appended per fragment/part from BUFFER_APPENDED events. */
  onBufferAppended(data: BufferAppendedData) {
    const chunkSize = data.chunkMeta.size;
    const partKey = getFragmentBufferedBytesKey(
      data.parent,
      data.frag.level,
      data.frag.sn,
      data.part?.index ?? -1,
    );
    const rollupKey = getFragmentBufferedBytesRollupKey(data.parent, data.frag.level, data.frag.sn);
    this.fragmentChunkBytes.set(partKey, (this.fragmentChunkBytes.get(partKey) ?? 0) + chunkSize);
    this.fragmentChunkBytes.set(
      rollupKey,
      (this.fragmentChunkBytes.get(rollupKey) ?? 0) + chunkSize,
    );
  }

  /** Drops chunk-byte entries for fragments no longer overlapping any buffered range. */
  pruneFragmentChunkBytes() {
    const keysToDelete: string[] = [];
    this.fragmentChunkBytes.forEach((_bytes, key) => {
      const fragment = this.findFragmentByKey(key);
      if (!fragment || !this.isFragmentInAnyBuffer(fragment)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach((key) => this.fragmentChunkBytes.delete(key));
  }

  private findFragmentByKey(key: string): MediaFragment | undefined {
    const [parent, levelStr, snValue] = key.split('|');
    const level = Number(levelStr);
    const sn = snValue === 'initSegment' ? 'initSegment' : Number(snValue);
    const playlists =
      parent === 'audio'
        ? this.hls.audioTracks.map((track) => track.details)
        : this.hls.levels.map((entry) => entry.details);

    for (const details of playlists) {
      const fragment = details?.fragments.find(
        (entry) => entry.level === level && `${entry.sn}` === `${sn}`,
      );
      if (fragment) {
        return fragment;
      }
    }

    return undefined;
  }

  private isFragmentInAnyBuffer(fragment: MediaFragment): boolean {
    for (const state of this.bufferRowStates.values()) {
      if (getFragmentBufferOverlapRatio(fragment, state.ranges) > 0) {
        return true;
      }
    }
    return false;
  }

  updateLevels(levelSwitched?: number) {
    const { levels, currentLevel, loadLevel } = this.hls;
    this.playlistRows = [
      ...buildLevelPlaylistRows(levels, levelSwitched ?? currentLevel, loadLevel),
      ...buildAudioPlaylistRows(this.hls.audioTracks, this.hls.audioTrack),
      ...buildSubtitlePlaylistRows(this.hls.subtitleTracks, this.hls.subtitleTrack),
    ];
  }

  updateAudioTracks() {
    const levelRows = this.playlistRows.filter((row) => row.playlistKind === 'level');
    const subtitleRows = this.playlistRows.filter((row) => row.playlistKind === 'subtitleTrack');
    this.playlistRows = [
      ...levelRows,
      ...buildAudioPlaylistRows(this.hls.audioTracks, this.hls.audioTrack),
      ...subtitleRows,
    ];
  }

  updateSubtitleTracks() {
    const levelRows = this.playlistRows.filter((row) => row.playlistKind === 'level');
    const audioRows = this.playlistRows.filter((row) => row.playlistKind === 'audioTrack');
    this.playlistRows = [
      ...levelRows,
      ...audioRows,
      ...buildSubtitlePlaylistRows(this.hls.subtitleTracks, this.hls.subtitleTrack),
    ];
  }

  updateLevelOrTrack(details: LevelDetails) {
    const detailsUrl = stripDeliveryDirectives(details.url);
    this.playlistRows = this.playlistRows.map((row) => {
      if (!row.url || stripDeliveryDirectives(row.url) !== detailsUrl) {
        return row;
      }
      return {
        ...row,
        fragmentCount: details.fragments.length,
        partCount: details.partList?.length ?? 0,
        loadedFragments: details.fragments.filter((fragment) => fragment.stats?.loaded).length,
        loadedBytes: details.fragments.reduce(
          (sum, fragment) => sum + (fragment.stats?.loaded ?? 0),
          0,
        ),
        totalBytes: details.fragments.reduce(
          (sum, fragment) => sum + (fragment.stats?.total ?? fragment.byteLength ?? 0),
          0,
        ),
        bufferedBytes: 0,
        playlistDuration: details.totalduration,
        targetDuration: details.targetduration,
        live: details.live,
      };
    });
  }

  updateSourceBuffers(tracks: BufferCreatedTrackSet, media: HTMLMediaElement) {
    this.clearBufferListeners();
    this.removeSourceBuffers();
    this.media = media;

    const trackTypes = Object.keys(tracks).sort((type) => (type === 'video' ? 1 : -1));
    const mediaBufferId = 'media-buffer';

    trackTypes.forEach((type) => {
      const track = tracks[type as keyof BufferCreatedTrackSet];
      if (!track) {
        return;
      }

      const id = `${type}-buffer-${track.id}`;
      const state: BufferRowState = {
        id,
        bufferKind: type as HlsTimelineBufferRow['bufferKind'],
        trackId: track.id,
        ranges: timeRangesToTuples(track.buffer.buffered),
      };
      this.bufferRowStates.set(id, state);

      const onUpdate = () => {
        try {
          state.ranges = timeRangesToTuples(track.buffer.buffered);
          const mediaState = this.bufferRowStates.get(mediaBufferId);
          if (mediaState) {
            mediaState.ranges = timeRangesToTuples(media.buffered);
          }
        } catch (error) {
          console.warn(error);
        }
      };

      track.buffer.addEventListener('update', onUpdate);
      this.bufferCleanupFns.push(() => track.buffer.removeEventListener('update', onUpdate));
    });

    if (trackTypes.length === 0) {
      const onProgress = () => {
        const mediaState = this.bufferRowStates.get(mediaBufferId);
        if (mediaState) {
          mediaState.ranges = timeRangesToTuples(media.buffered);
        }
      };
      media.addEventListener('progress', onProgress);
      this.bufferCleanupFns.push(() => media.removeEventListener('progress', onProgress));
    }

    this.bufferRowStates.set(mediaBufferId, {
      id: mediaBufferId,
      bufferKind: 'media',
      ranges: timeRangesToTuples(media.buffered),
    });
  }

  removeSourceBuffers() {
    const bufferIds = [...this.bufferRowStates.keys()];
    bufferIds.forEach((id) => this.bufferRowStates.delete(id));
  }

  snapshot(): HlsTimelineStats {
    const currentTime = this.media?.currentTime ?? this.hls.media?.currentTime ?? null;
    const mediaRanges = this.bufferRowStates.get('media-buffer')?.ranges ?? [];
    const bufferRows = [...this.bufferRowStates.values()]
      .map((state) => {
        const row = buildBufferRow(
          state.bufferKind === 'media'
            ? 'media buffer'
            : `${state.bufferKind} buffer (${state.trackId})`,
          state.bufferKind,
          state.ranges,
          currentTime,
          state.trackId,
        );
        return {
          ...row,
          bufferedBytes: getBufferRowBufferedBytes(
            this.hls,
            state.bufferKind,
            state.ranges,
            this.fragmentChunkBytes,
          ),
        };
      })
      .sort((left, right) => {
        const order = { media: 0, video: 1, audiovideo: 2, audio: 3 };
        return order[left.bufferKind] - order[right.bufferKind];
      });

    const playlistRows = this.playlistRows.map((row) => ({
      ...row,
      bufferedBytes: getPlaylistBufferedBytes(
        getFragmentsForPlaylistRow(this.hls, row),
        mediaRanges,
        this.fragmentChunkBytes,
        row.playlistKind,
      ),
    }));

    const forwardInfo = this.hls.mainForwardBufferInfo;

    return createStatsSnapshot(
      bufferRows,
      playlistRows,
      currentTime,
      this.hls.maxBufferLength,
      forwardInfo
        ? {
            len: forwardInfo.len,
            start: forwardInfo.start,
            end: forwardInfo.end,
          }
        : null,
    );
  }
}

/**
 * Wires hls.js playback events to a stats collector.
 * Returns a cleanup function that removes all listeners.
 */
export function addHlsTimelineStatsListeners(
  hls: Hls,
  collector: HlsTimelineStatsCollector,
  onUpdate: () => void,
): () => void {
  const refresh = () => onUpdate();

  const onManifestLoading = () => {
    collector.reset();
    refresh();
  };

  const onManifestParsed = () => {
    collector.updateLevels();
    refresh();
  };

  const onBufferCreated = (_event: string, data: { tracks: BufferCreatedTrackSet }) => {
    if (hls.media) {
      collector.updateSourceBuffers(data.tracks, hls.media);
      refresh();
    }
  };

  const onBufferReset = () => {
    collector.removeSourceBuffers();
    refresh();
  };

  const onLevelsUpdated = () => {
    collector.updateLevels();
    refresh();
  };

  const onLevelSwitched = (_event: string, data: LevelSwitchedData) => {
    collector.updateLevels(data.level);
    refresh();
  };

  const onLevelLoading = () => {
    collector.updateLevels();
    refresh();
  };

  const onLevelUpdated = (_event: string, data: LevelUpdatedData) => {
    collector.updateLevelOrTrack(data.details);
    refresh();
  };

  const onLevelPtsUpdated = (_event: string, data: LevelPTSUpdatedData) => {
    collector.updateLevelOrTrack(data.details);
    refresh();
  };

  const onAudioTracksUpdated = () => {
    collector.updateAudioTracks();
    refresh();
  };

  const onSubtitleTracksUpdated = () => {
    collector.updateSubtitleTracks();
    refresh();
  };

  const onAudioTrackSwitched = () => {
    collector.updateAudioTracks();
    refresh();
  };

  const onSubtitleTrackSwitch = () => {
    collector.updateSubtitleTracks();
    refresh();
  };

  const onAudioTrackLoaded = (_event: string, data: AudioTrackLoadedData) => {
    collector.updateLevelOrTrack(data.details);
    refresh();
  };

  const onSubtitleTrackLoaded = (_event: string, data: SubtitleTrackLoadedData) => {
    collector.updateLevelOrTrack(data.details);
    refresh();
  };

  hls.on(Hls.Events.MANIFEST_LOADING, onManifestLoading);
  hls.on(Hls.Events.MANIFEST_PARSED, onManifestParsed);
  hls.on(Hls.Events.BUFFER_CREATED, onBufferCreated);
  hls.on(Hls.Events.BUFFER_RESET, onBufferReset);
  hls.on(Hls.Events.LEVELS_UPDATED, onLevelsUpdated);
  hls.on(Hls.Events.LEVEL_SWITCHED, onLevelSwitched);
  hls.on(Hls.Events.LEVEL_LOADING, onLevelLoading);
  hls.on(Hls.Events.LEVEL_UPDATED, onLevelUpdated);
  hls.on(Hls.Events.LEVEL_PTS_UPDATED, onLevelPtsUpdated);
  hls.on(Hls.Events.AUDIO_TRACKS_UPDATED, onAudioTracksUpdated);
  hls.on(Hls.Events.SUBTITLE_TRACKS_UPDATED, onSubtitleTracksUpdated);
  hls.on(Hls.Events.AUDIO_TRACK_SWITCHED, onAudioTrackSwitched);
  hls.on(Hls.Events.SUBTITLE_TRACK_SWITCH, onSubtitleTrackSwitch);
  hls.on(Hls.Events.AUDIO_TRACK_LOADED, onAudioTrackLoaded);
  hls.on(Hls.Events.SUBTITLE_TRACK_LOADED, onSubtitleTrackLoaded);
  const onFragmentUpdate = () => {
    collector.updateLevels(hls.currentLevel);
    refresh();
  };

  hls.on(Hls.Events.FRAG_LOADED, onFragmentUpdate);
  hls.on(Hls.Events.FRAG_PARSED, onFragmentUpdate);
  hls.on(Hls.Events.FRAG_CHANGED, onFragmentUpdate);
  const onBufferAppended = (_event: string, data: BufferAppendedData) => {
    collector.onBufferAppended(data);
    refresh();
  };

  const onBufferFlushed = () => {
    collector.pruneFragmentChunkBytes();
    refresh();
  };

  hls.on(Hls.Events.BUFFER_APPENDING, refresh);
  hls.on(Hls.Events.BUFFER_APPENDED, onBufferAppended);
  hls.on(Hls.Events.BUFFER_FLUSHED, onBufferFlushed);

  if (hls.levels?.length) {
    collector.updateLevels(hls.currentLevel);
  }
  if (hls.audioTracks?.length) {
    collector.updateAudioTracks();
  }
  if (hls.media) {
    collector.updateSourceBuffers({}, hls.media);
  }

  const media = hls.media;
  if (media) {
    media.addEventListener('timeupdate', refresh);
    media.addEventListener('progress', refresh);
  }

  refresh();

  return () => {
    hls.off(Hls.Events.MANIFEST_LOADING, onManifestLoading);
    hls.off(Hls.Events.MANIFEST_PARSED, onManifestParsed);
    hls.off(Hls.Events.BUFFER_CREATED, onBufferCreated);
    hls.off(Hls.Events.BUFFER_RESET, onBufferReset);
    hls.off(Hls.Events.LEVELS_UPDATED, onLevelsUpdated);
    hls.off(Hls.Events.LEVEL_SWITCHED, onLevelSwitched);
    hls.off(Hls.Events.LEVEL_LOADING, onLevelLoading);
    hls.off(Hls.Events.LEVEL_UPDATED, onLevelUpdated);
    hls.off(Hls.Events.LEVEL_PTS_UPDATED, onLevelPtsUpdated);
    hls.off(Hls.Events.AUDIO_TRACKS_UPDATED, onAudioTracksUpdated);
    hls.off(Hls.Events.SUBTITLE_TRACKS_UPDATED, onSubtitleTracksUpdated);
    hls.off(Hls.Events.AUDIO_TRACK_SWITCHED, onAudioTrackSwitched);
    hls.off(Hls.Events.SUBTITLE_TRACK_SWITCH, onSubtitleTrackSwitch);
    hls.off(Hls.Events.AUDIO_TRACK_LOADED, onAudioTrackLoaded);
    hls.off(Hls.Events.SUBTITLE_TRACK_LOADED, onSubtitleTrackLoaded);
    hls.off(Hls.Events.FRAG_LOADED, onFragmentUpdate);
    hls.off(Hls.Events.FRAG_PARSED, onFragmentUpdate);
    hls.off(Hls.Events.FRAG_CHANGED, onFragmentUpdate);
    hls.off(Hls.Events.BUFFER_APPENDING, refresh);
    hls.off(Hls.Events.BUFFER_APPENDED, onBufferAppended);
    hls.off(Hls.Events.BUFFER_FLUSHED, onBufferFlushed);

    if (media) {
      media.removeEventListener('timeupdate', refresh);
      media.removeEventListener('progress', refresh);
    }

    collector.destroy();
  };
}
